import { useState, useEffect } from "react";
import { Facebook, Instagram, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from '../../i18n/LanguageContext';
import axios_instance from '../../api/axios_instance';

function Footer() {
  const currentYear = new Date().getFullYear();
  const { t, dir, getLabel } = useLanguage();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios_instance.get('/api/categories').then(res => {
      const all = res.data ?? [];
      setCategories(all.filter(c => !c.parentCategory));
    }).catch(() => {});
  }, []);

  return (
    <footer className="bg-gray-50 border-t border-gray-100" dir={dir}>

      {/* ── Newsletter banner ── */}
      <div className="bg-emerald-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-center gap-3 max-w-2xl mx-auto w-full">
            <h3 className="text-white text-sm font-black shrink-0">{t('footer.newsletterTitle')}</h3>
            <div className="flex flex-1 gap-2">
              <input
                type="email"
                placeholder={t('footer.newsletterPlaceholder')}
                className="flex-1 w-full md:max-w-md bg-white/20 border border-white/30 rounded-lg px-4 py-2.5 text-xs text-white placeholder-emerald-200 outline-none focus:border-white transition-colors"
              />
              <button className="bg-white hover:bg-emerald-50 text-emerald-600 px-3 py-1.5 rounded-lg font-bold text-xs transition-colors flex items-center gap-1.5 shrink-0">
                <Send size={11} />
                {t('footer.newsletterBtn')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">

          {/* Brand column */}
          <div className="lg:col-span-2 space-y-5">
            <Link to="/">
              <img src="/logo.png" alt="BenMoussa" className="h-14 w-auto" />
            </Link>
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>

            {/* Social icons */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://www.facebook.com/profile.php?id=100064971490075"
                target="_blank" rel="noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-[#1877F2] hover:border-[#1877F2] rounded-xl flex items-center justify-center transition-all group"
              >
                <Facebook size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://www.instagram.com/bms.bio1"
                target="_blank" rel="noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-[#E4405F] hover:border-[#E4405F] rounded-xl flex items-center justify-center transition-all group"
              >
                <Instagram size={18} className="text-gray-400 group-hover:text-white transition-colors" />
              </a>
              <a
                href="https://www.tiktok.com/@yassinebenmousa"
                target="_blank" rel="noreferrer"
                aria-label="TikTok"
                className="w-10 h-10 bg-white border border-gray-200 hover:bg-black hover:border-black rounded-xl flex items-center justify-center transition-all group"
              >
                <svg className="w-[18px] h-[18px] fill-gray-400 group-hover:fill-white transition-colors" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.27 8.27 0 0 0 4.84 1.55V6.78a4.85 4.85 0 0 1-1.07-.09z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Categories */}
          <div className="lg:col-span-2 space-y-4">
            <h4 className="text-gray-900 font-black text-sm uppercase tracking-wider">
              {t('footer.categoriesTitle')}
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {categories.map((cat) => (
                <li key={cat._id}>
                  <Link to={`/products/category/${cat.slug}`} className="text-gray-500 hover:text-emerald-600 transition-colors flex items-center gap-2 group">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    {getLabel(cat.name)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* ── Bottom bar ── */}
      <div className="border-t border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row items-center justify-between gap-4">
     

          <p className="text-gray-400 text-xs text-center">
            © {currentYear} BenMoussa — {t('footer.allRights')}
          </p>

        </div>
      </div>
    </footer>
  );
}

export default Footer;
